<template>
  <div v-if="pharmaMLData" class="flex flex-col gap-4 text-lg bg-white shadow p-3 rounded-lg">
    <div class="flex flex-row gap-1">
      <p>Taux de dispo</p>
      <TrendingUp v-if="isTrendUp" class="text-secondary" />
      <TrendingDown v-else class="text-red" />
    </div>
    <div class="flex flex-row justify-around">
      <p class="font-semibold text-xl">
        <span class="text-primary">
          {{ twentyFourHours }}%
        </span>
        sur 24 heures
      </p>
      <p class="font-semibold text-xl">
        <span class="text-primary">
          {{ sevenDays }}%
        </span>
        sur 7 jours
      </p>
      <p class="font-semibold text-xl">
        <span class="text-primary">
          {{ thirtyDays }}%
        </span>
        sur 30 jours
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {disruptionLogisticsApi} from "@/container";
import {AVAILABILITY_PROVIDERS} from "@/core/http/resources/DisruptionLogisticsResource";
import {TrendingDown, TrendingUp} from "@lucide/vue";

import {
    DepositaryDetails,
    HealthcenterGroupDistributorDetails,
    PharmaML
} from "@/disruption-logistics/interfaces/availability-providers";
import {HealthcenterGroup} from "@/disruption-logistics/interfaces/healthcenter-groups";
import {computed, ref, watch} from "vue";
import {numberFormat} from "@/core/filters/NumberFormat";

export type AvailabilityState<T> = {
  loading: boolean,
  availability: boolean | null,
  is_available?: boolean | null,
  messages: string[] | null
  details: T
}

const props = defineProps<{productId: number, requestToken: string, pharmaML?: PharmaML}>();
const emits = defineEmits(['availability-loaded']);

const pharmaMLData = ref<PharmaML|null>(props.pharmaML ?? null);

watch(() => props.pharmaML, (newValue) => {
    if (newValue) {
        pharmaMLData.value = newValue;
    }
});

const medidestock = ref<AvailabilityState<undefined>>({
    loading: true,
    availability: null,
    messages: null,
    details: undefined
});
const groups = ref<AvailabilityState<HealthcenterGroup[]>>({
    loading: true,
    availability: null,
    messages: null,
    details: []
});
const directAvailability = ref<AvailabilityState<DepositaryDetails>>({
    loading: true,
    availability: null,
    messages: null,
    details: {} as DepositaryDetails
});
const comptoirDesPharmacies = ref<AvailabilityState<undefined>>({
    loading: true,
    availability: null,
    messages: null,
    details: undefined
});
const groupWarehouses = ref<AvailabilityState<HealthcenterGroupDistributorDetails[]>>({
    loading: true,
    availability: null,
    messages: null,
    details: []
});

const refsByKey = {
    medidestock,
    groups,
    directAvailability,
    comptoirDesPharmacies,
    groupWarehouses
};

const twentyFourHours = computed(() => {
    if (!pharmaMLData.value) {
        return 0;
    }

    return numberFormat(
        rate(
            pharmaMLData.value.last_twenty_four_hours_statistics.total_delivered_quantity,
            pharmaMLData.value.last_twenty_four_hours_statistics.total_ordered_quantity
        ), 0
    );
});

const sevenDays = computed(() => {
    if (!pharmaMLData.value) {
        return 0;
    }

    return numberFormat(
        rate(
            pharmaMLData.value.last_seven_day_statistics.total_delivered_quantity,
            pharmaMLData.value.last_seven_day_statistics.total_ordered_quantity
        ), 0
    );
});

const thirtyDays = computed(() => {
    if (!pharmaMLData.value) {
        return 0;
    }

    return numberFormat(
        rate(
            pharmaMLData.value.last_thirty_day_statistics.total_delivered_quantity,
            pharmaMLData.value.last_thirty_day_statistics.total_ordered_quantity
        ), 0
    );
});

const isTrendUp = computed(() => {
    if (typeof twentyFourHours.value === "string" && typeof  sevenDays.value === "string") {
        return parseInt(twentyFourHours.value) > parseInt(sevenDays.value);
    }

    return twentyFourHours.value > sevenDays.value;
});

const loadAvailabilityAndEmit = async (provider: string, productId: number, alias: string | null = null): Promise<void> => {
    return loadAvailability(provider, productId, alias)
        .then((data) => {
            emits('availability-loaded', {provider, data});
        });
};

/**
* Load the availability of the product from the provider.
*
* @param provider - The provider name
* @param productId - The product id
* @param alias - The state alias
*
* @returns The availability data
*/
const loadAvailability = async (provider: string, productId: number, alias: null | string = null) => {
    const key = alias ?? provider;
    refsByKey[key].value.loading = true;
    return disruptionLogisticsApi()
        .productAvailability(provider, productId, props.requestToken)
        .then((availability) => {
            refsByKey[key].value.availability = availability.is_available;
            refsByKey[key].value.messages = availability.messages ?? null;
            refsByKey[key].value.details = availability.details ?? null;

            return availability;
        })
        .finally(() => {
            refsByKey[key].value.loading = false;
        });
};


watch(() => props.productId, () => {
    loadAvailabilityAndEmit(AVAILABILITY_PROVIDERS.medidestock, props.productId);
    loadAvailabilityAndEmit(AVAILABILITY_PROVIDERS.leComptoirDesPharmacies, props.productId, 'comptoirDesPharmacies');
    loadAvailabilityAndEmit(AVAILABILITY_PROVIDERS.healthcenterGroups, props.productId, 'groups');
    loadAvailabilityAndEmit(AVAILABILITY_PROVIDERS.direct, props.productId, 'directAvailability');
    loadAvailabilityAndEmit(AVAILABILITY_PROVIDERS.healthcenterGroupsDistributor, props.productId, 'groupWarehouses');
}, {immediate: true});

/**
 * Get the rate of the delivered quantity.
 */
const rate = (deliveredQuantity: number, orderedQuantity: number): number => {
    const _rate = (deliveredQuantity / orderedQuantity) * 100;

    return isNaN(_rate) ? 0 : _rate;
};
</script>
